import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData, postData, updateData } from '../../../apiService';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider, Chip, CircularProgress,
  InputAdornment, Pagination, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar, Alert, Autocomplete, Popover, Tooltip
} from '@mui/material';
import { ArrowBack, Add, Delete, Search, List, Warning, Save, ExpandMore, ExpandLess, KeyboardArrowDown, Check } from '@mui/icons-material';
import LogoLoader from '../../common/LogoLoader';
import './EnquiryForm.css';

const EnquiryForm = () => {
  useSessionValidation();
  const history = useHistory();
  const { id, action } = useParams(); // /EnquiryForm/:action/:id?
  const { companyId, branchId, token, userId } = useAuth();
  const { logAudit } = useAuditLog();

  const isViewMode = action === 'view';
  const isEditMode = action === 'edit';
  const isCreateMode = action === 'create';
  const title = isCreateMode ? 'New Enquiry' : isEditMode ? 'Edit Enquiry' : 'View Enquiry';

  // States
  const [formData, setFormData] = useState({});
  const [enquiryDetails, setEnquiryDetails] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Get Items modal
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearch, setGetItemsSearch] = useState('');
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [itemsPerPage] = useState(50);

  // Get Items — brand/category filter
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterBrandId, setFilterBrandId] = useState(null);
  const [filterCategoryId, setFilterCategoryId] = useState(null);
  const [filteredByCategory, setFilteredByCategory] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);

  // Back confirmation
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [backConfirmOpen, setBackConfirmOpen] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});
  const [originalDetails, setOriginalDetails] = useState([]);

  // Customer section expand/collapse
  const [customerExpanded, setCustomerExpanded] = useState(true);

  // Filter card expand/collapse
  const [filterExpanded, setFilterExpanded] = useState(true);

  useEffect(() => {
    if (companyId && token) {
      loadData();
    }
  }, [companyId, token, id, action]);

  const loadData = async () => {
    setLoading(true);
    setLoadingMessage('Loading...');

    // Fetch items
    try {
      const itemsRes = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(itemsRes.response_data || itemsRes || []);
    } catch (e) {
      console.error('Error fetching items:', e);
    }

    // Fetch brands & categories for filter
    try {
      const [brandsRes, catsRes] = await Promise.allSettled([
        fetchData('BrandApi', `/${companyId}`, token),
        fetchData('CategoryApi', `/${companyId}`, token)
      ]);
      if (brandsRes.status === 'fulfilled') setBrands(brandsRes.value.response_data || brandsRes.value || []);
      if (catsRes.status === 'fulfilled') setCategories(catsRes.value.response_data || catsRes.value || []);
    } catch (e) {
      console.error('Error fetching brands/categories:', e);
    }

    // Fetch customers (for phone number search)
    try {
      const custRes = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
      setCustomers(custRes.response_data || custRes || []);
    } catch (e) {
      console.error('Error fetching customers:', e);
    }

    if (isCreateMode) {
      // Fetch max bill no
      try {
        const response = await fetchData('EnquiryApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
        const data = response.response_data || response || {};
        const billNumber = data.BillNumber || data.billNumber || 1;
        const billPrefix = data.BillPrefix || data.billPrefix || '';
        const finYear = data.FinYear || data.finYear || '';
        let enquiryNo = '';
        if (billPrefix && finYear) enquiryNo = `${billPrefix}/${billNumber}/${finYear}`;
        else if (finYear) enquiryNo = `${billNumber}/${finYear}`;
        else if (billPrefix) enquiryNo = `${billPrefix}/${billNumber}`;
        else enquiryNo = `${billNumber}`;

        const newForm = {
          CompanyId: companyId, BranchId: branchId || 1, MasterId: null,
          EnquiryNumber: billNumber, EnquiryNo: enquiryNo, EnquiryPrefix: billPrefix,
          FinYear: finYear, EnquiryDate: new Date().toISOString().split('T')[0],
          CustomerName: '', ContactNumber: '', Address: '',
          TotalQuantity: 0, TotalAmount: 0, IsActive: true, IsSaled: false
        };
        setFormData(newForm);
        setOriginalFormData(newForm);
        setEnquiryDetails([]);
        setOriginalDetails([]);
      } catch (e) {
        console.error('Error fetching max bill:', e);
      }
    } else if (id) {
      // Fetch existing enquiry
      try {
        const response = await fetchData('EnquiryApi', `/${id}`, token);
        const freshData = response.response_data || response;
        const enquiryData = freshData.Enquiry || freshData;
        const details = freshData.EnquiryDetails || freshData.Details || [];

        const form = {
          Id: enquiryData.Id, CompanyId: enquiryData.CompanyId || companyId,
          BranchId: enquiryData.BranchId || branchId, MasterId: enquiryData.MasterId || null,
          EnquiryNumber: enquiryData.EnquiryNumber || 0,
          EnquiryNo: enquiryData.EnquiryNo || '', EnquiryPrefix: enquiryData.EnquiryPrefix || '',
          FinYear: enquiryData.FinYear || '',
          EnquiryDate: enquiryData.EnquiryDate?.split('T')[0] || '',
          CustomerName: enquiryData.CustomerName || '', ContactNumber: enquiryData.ContactNumber || '',
          Address: enquiryData.Address || '', TotalQuantity: enquiryData.TotalQuantity || 0,
          TotalAmount: enquiryData.TotalAmount || 0, IsActive: enquiryData.IsActive ?? true,
          IsSaled: enquiryData.IsSaled ?? false,
          IsConfirmOrder: enquiryData.IsConfirmOrder ?? false,
          CreatedBy: enquiryData.CreatedBy || null, CreatedDate: enquiryData.CreatedDate || null
        };
        setFormData(form);
        setOriginalFormData(form);

        const mappedDetails = details.map(d => ({
          Id: d.Id || 0, ItemId: d.ItemId, HSNCode: d.HSNCode || '', GSTRate: d.GSTRate || 0,
          Quantity: d.Quantity || 0, Rate: d.Rate || 0, Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0, DiscountAmount: d.DiscountAmount || 0,
          TaxableAmount: d.TaxableAmount || 0, CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0, IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || 0, WhichMOP: d.WhichMOP || null, IsActive: d.IsActive ?? true,
          StarRating: d.StarRating || 0,
          CreatedBy: d.CreatedBy || null, CreatedDate: d.CreatedDate || null
        }));
        setEnquiryDetails(mappedDetails);
        setOriginalDetails(mappedDetails);
      } catch (e) {
        console.error('Error fetching enquiry:', e);
        setError('Error loading enquiry');
      }
    }
    setLoading(false);
  };

  // Track changes
  const changeTrackRef = useRef(null);
  useEffect(() => {
    if (!isViewMode && Object.keys(originalFormData).length > 0) {
      clearTimeout(changeTrackRef.current);
      changeTrackRef.current = setTimeout(() => {
        const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
        const hasDetailChanges = JSON.stringify(enquiryDetails) !== JSON.stringify(originalDetails);
        setHasUnsavedChanges(hasFormChanges || hasDetailChanges);
      }, 300);
    }
    return () => clearTimeout(changeTrackRef.current);
  }, [formData, enquiryDetails, originalFormData, originalDetails, isViewMode]);

  const handleBack = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setBackConfirmOpen(true);
    } else {
      history.push('/EnquiryIndex');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Phone number search — show matching customers in dropdown
  const [customerFound, setCustomerFound] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Offers/Warranty state
  const [itemOffersWarranty, setItemOffersWarranty] = useState({});
  const [offersModalOpen, setOffersModalOpen] = useState(false);
  const [offersModalData, setOffersModalData] = useState([]);
  const [offersModalItemName, setOffersModalItemName] = useState('');

  // Rate Selection (MOP) state — now using inline popover dropdown
  const [rateModalOpen, setRateModalOpen] = useState(false);
  const [rateModalLoading, setRateModalLoading] = useState(false);
  const [rateOptions, setRateOptions] = useState([]);
  const [rateModalItemName, setRateModalItemName] = useState('');
  const [rateModalIndex, setRateModalIndex] = useState(null);

  // MOP dropdown popover per row
  const [mopAnchorEl, setMopAnchorEl] = useState(null);
  const [mopPopoverIndex, setMopPopoverIndex] = useState(null);
  const [mopRowOptions, setMopRowOptions] = useState([]);
  const [mopRowLoading, setMopRowLoading] = useState(false);
  // cache fetched mop options per itemId
  const mopCacheRef = useRef({});

  const handleSelectCustomer = (event, customer) => {
    if (customer) {
      setFormData(prev => ({
        ...prev,
        ContactNumber: customer.ContactNumber1 || '',
        CustomerName: customer.Name || customer.CustomerName || '',
        Address: customer.Address || customer.Address1 || '',
        MasterId: customer.MasterId || customer.Id || null
      }));
      setSelectedCustomer(customer);
      setCustomerFound(true);
    } else {
      setFormData(prev => ({ ...prev, ContactNumber: '', CustomerName: '', Address: '', MasterId: null }));
      setSelectedCustomer(null);
      setCustomerFound(false);
    }
  };

  // Calculate totals
  const calculateTotals = useCallback((details) => {
    const totals = details.reduce((acc, d) => ({
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0),
      TotalQuantity: acc.TotalQuantity + (parseFloat(d.Quantity) || 0)
    }), { TotalAmount: 0, TotalQuantity: 0 });
    setFormData(prev => ({
      ...prev, TotalQuantity: totals.TotalQuantity,
      TotalAmount: Math.round(totals.TotalAmount * 100) / 100
    }));
  }, []);

  const handleDetailChange = useCallback((index, field, value) => {
    const updatedDetails = [...enquiryDetails];
    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: 0 };
        setEnquiryDetails(updatedDetails);
        calculateTotals(updatedDetails);
        return;
      }
      const validNumberRegex = /^\d*\.?\d*$/;
      if (!validNumberRegex.test(value)) return;
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;
      if (field === 'DiscountPercentage' && numValue > 100) return;
    }
    updatedDetails[index] = { ...updatedDetails[index], [field]: value };

    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      const detail = updatedDetails[index];
      const quantity = parseFloat(detail.Quantity) || 0;
      const rate = parseFloat(detail.Rate) || 0;
      const gstRate = parseFloat(detail.GSTRate) || 0;
      const amount = quantity * rate;

      let discountAmount = 0, discountPct = 0;
      if (field === 'DiscountAmount') {
        discountAmount = Math.max(0, Math.min(parseFloat(value) || 0, amount));
        discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
      } else {
        discountPct = Math.max(0, Math.min(parseFloat(detail.DiscountPercentage) || 0, 100));
        discountAmount = (amount * discountPct) / 100;
      }
      const netAmount = amount - discountAmount;
      const taxableAmount = gstRate > 0 ? netAmount / (1 + gstRate / 100) : netAmount;
      const gstAmount = netAmount - taxableAmount;

      updatedDetails[index] = {
        ...updatedDetails[index], Amount: amount,
        DiscountPercentage: Math.round(discountPct * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: Math.round(taxableAmount * 100) / 100,
        CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
        SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
        IGSTAmount: 0, TotalAmount: Math.round(netAmount * 100) / 100
      };
    }
    setEnquiryDetails(updatedDetails);
    calculateTotals(updatedDetails);
  }, [enquiryDetails, calculateTotals]);

  const handleItemSelect = useCallback((index, itemId) => {
    const selectedItem = items.find(i => i.Id === parseInt(itemId));
    if (selectedItem) {
      const updatedDetails = [...enquiryDetails];
      const quantity = parseFloat(updatedDetails[index].Quantity) || 0;
      const rate = selectedItem.SaleRate || selectedItem.PurchaseRate || 0;
      const gstRate = selectedItem.GSTRate || 0;
      const discountPct = parseFloat(updatedDetails[index].DiscountPercentage) || 0;
      const amount = quantity * rate;
      const discountAmount = (amount * discountPct) / 100;
      const netAmount = amount - discountAmount;
      const taxableAmount = gstRate > 0 ? netAmount / (1 + gstRate / 100) : netAmount;
      const gstAmount = netAmount - taxableAmount;

      updatedDetails[index] = {
        ...updatedDetails[index], ItemId: itemId, HSNCode: selectedItem.HSNCode || '',
        StarRating: selectedItem.StarRating || 0,
        GSTRate: gstRate, Rate: rate, Amount: amount,
        DiscountPercentage: Math.round(discountPct * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: Math.round(taxableAmount * 100) / 100,
        CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
        SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
        IGSTAmount: 0, TotalAmount: Math.round(netAmount * 100) / 100
      };
      setEnquiryDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  }, [enquiryDetails, items, calculateTotals]);

  const fetchItemOffersWarranty = async (itemId) => {
    try {
      const response = await fetchData('ItemOffersAndWarrantyApi', `/ItemOffersAndWarrantyList/${itemId}`, token);
      const data = response.response_data || response || [];
      if (data && (Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0)) {
        setItemOffersWarranty(prev => ({ ...prev, [itemId]: Array.isArray(data) ? data : [data] }));
      }
    } catch (e) {
      console.error('Error fetching offers/warranty:', e);
    }
  };

  // Build MOP options from item data
  // MOP1..MOP5 are stored as percentages; rate = dealersPrice * MOPx% / 100
  const buildMopOptions = (itemData) => {
    const dealersPrice = parseFloat(itemData.DealersPrice) || 0;
    const options = [];

    const addMop = (label, pct, fallbackRate) => {
      const p = parseFloat(pct) || 0;
      if (p > 0 && dealersPrice > 0) {
        // rate = dealersPrice * MOPx% / 100 + dealersPrice
        options.push({ label, value: Math.round((dealersPrice * p / 100 + dealersPrice) * 100) / 100, pct: p });
      } else if (fallbackRate && parseFloat(fallbackRate) > 0) {
        // fallback: stored as fixed rate (no dealersPrice available or pct=0)
        options.push({ label, value: parseFloat(fallbackRate), pct: null });
      }
    };

    addMop('MOP1', itemData.MOP1);
    addMop('MOP2', itemData.MOP2);
    addMop('MOP3', itemData.MOP3);
    addMop('MOP4', itemData.MOP4);
    addMop('MOP5', itemData.MOP5);

    // MRP and SaleRate are fixed values, not percentages
    if (itemData.MRP && parseFloat(itemData.MRP) > 0)
      options.push({ label: 'MRP', value: parseFloat(itemData.MRP), pct: null });
    if (itemData.SaleRate && parseFloat(itemData.SaleRate) > 0)
      options.push({ label: 'Sale Rate', value: parseFloat(itemData.SaleRate), pct: null });

    return options;
  };

  // Returns only the visible MOP options for a row based on progressive reveal:
  // - Nothing selected yet → show only first option
  // - Something selected → show options up to selected index + 1 next (if exists)
  const getVisibleMopOptions = (allOptions, currentWhichMOP) => {
    if (!allOptions || allOptions.length === 0) return [];
    if (!currentWhichMOP) return allOptions.slice(0, 1); // only first
    const selectedIdx = allOptions.findIndex(o => o.label === currentWhichMOP);
    if (selectedIdx === -1) return allOptions.slice(0, 1);
    // show up to selected + 1 next
    return allOptions.slice(0, selectedIdx + 2);
  };

  // Open MOP dropdown popover for a row
  const handleMopDropdownOpen = async (event, index) => {
    if (isViewMode) return;
    const detail = enquiryDetails[index];
    if (!detail.ItemId) return;
    event.stopPropagation();

    setMopAnchorEl(event.currentTarget);
    setMopPopoverIndex(index);

    // Use cache if available
    if (mopCacheRef.current[detail.ItemId]) {
      const visible = getVisibleMopOptions(mopCacheRef.current[detail.ItemId], detail.WhichMOP);
      setMopRowOptions(visible);
      setMopRowLoading(false);
      return;
    }

    setMopRowLoading(true);
    setMopRowOptions([]);
    try {
      const response = await fetchData('ItemApi', `/Item/${detail.ItemId}`, token);
      const data = response?.response_data || response || {};
      const itemData = data.Item || data;
      const allOptions = buildMopOptions(itemData);
      mopCacheRef.current[detail.ItemId] = allOptions; // cache full list
      const visible = getVisibleMopOptions(allOptions, detail.WhichMOP);
      setMopRowOptions(visible);
    } catch (e) {
      console.error('Error fetching MOP options:', e);
      setMopRowOptions([]);
    } finally {
      setMopRowLoading(false);
    }
  };

  const handleMopDropdownClose = () => {
    setMopAnchorEl(null);
    setMopPopoverIndex(null);
  };

  const handleMopSelect = (option) => {
    if (mopPopoverIndex !== null) {
      handleDetailChange(mopPopoverIndex, 'Rate', option.value.toString());
      setEnquiryDetails(prev => {
        const updated = [...prev];
        updated[mopPopoverIndex] = { ...updated[mopPopoverIndex], WhichMOP: option.label };
        return updated;
      });
    }
    handleMopDropdownClose();
  };

  // legacy — kept to avoid unused-ref warnings
  const handleRateClick = () => { };

  const handleRateSelect = (rate, label) => {
    if (rateModalIndex !== null) {
      handleDetailChange(rateModalIndex, 'Rate', rate.toString());
      setEnquiryDetails(prev => {
        const updated = [...prev];
        updated[rateModalIndex] = { ...updated[rateModalIndex], WhichMOP: label || null };
        return updated;
      });
    }
    setRateModalOpen(false);
    setRateModalIndex(null);
  };

  const handleOpenOffersModal = async (itemId) => {
    setOffersModalItemName(itemNameMap[itemId] || 'Item');
    setOffersModalData([]);
    setOffersModalOpen(true);
    // Fetch on click
    try {
      const response = await fetchData('ItemOffersAndWarrantyApi', `/ItemOffersAndWarrantyList/${itemId}`, token);
      const data = response.response_data || response || [];
      setOffersModalData(Array.isArray(data) ? data : [data]);
    } catch (e) {
      console.error('Error fetching offers/warranty:', e);
      setOffersModalData([]);
    }
  };

  const removeDetailRow = useCallback((index) => {
    setEnquiryDetails(prev => {
      const updatedDetails = prev.filter((_, i) => i !== index);
      calculateTotals(updatedDetails);
      return updatedDetails;
    });
  }, [calculateTotals]);

  // Get Items Modal handlers
  const handleOpenGetItemsModal = async () => {
    setSelectedItems([]);
    setGetItemsSearch('');
    setCurrentItemsPage(1);
    setFilteredByCategory([]);
    // If brand or category selected, fetch filtered items first then open modal
    if (filterBrandId || filterCategoryId) {
      const bId = filterBrandId || 0;
      const cId = filterCategoryId || 0;
      setLoading(true);
      setLoadingMessage('Loading items...');
      try {
        const res = await fetchData('SchemeApi', `/BrandAndCategoryWiseItemList/${companyId}/${branchId}/${bId}/${cId}`, token);
        setFilteredByCategory(res.response_data || res || []);
      } catch (e) {
        setFilteredByCategory([]);
      } finally {
        setLoading(false);
      }
    }
    setGetItemsModalOpen(true);
  };

  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearch('');
    setFilteredByCategory([]);
    // Keep filterBrandId / filterCategoryId so user can reuse same filter
  };

  // filtered items source: brand/category filter active → use filteredByCategory, else full items

  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      setSelectedItems(prev => [...prev, {
        ...item, selectedQuantity: 1, selectedRate: item.SaleRate || 0, selectedDiscountPercentage: 0
      }]);
    } else {
      setSelectedItems(prev => prev.filter(s => s.Id !== item.Id));
    }
  };

  const handleAddSelectedItems = () => {
    if (selectedItems.length === 0) return;
    const newItems = selectedItems
      .filter(si => !enquiryDetails.some(d => d.ItemId === si.Id))
      .map(si => {
        const quantity = parseFloat(si.selectedQuantity) || 0;
        const rate = parseFloat(si.selectedRate) || 0;
        const gstRate = parseFloat(si.GSTRate) || 0;
        const discPct = parseFloat(si.selectedDiscountPercentage) || 0;
        const amount = quantity * rate;
        const discountAmount = (amount * discPct) / 100;
        const netAmount = amount - discountAmount;
        const taxableAmount = gstRate > 0 ? netAmount / (1 + gstRate / 100) : netAmount;
        const gstAmount = netAmount - taxableAmount;
        return {
          Id: 0, ItemId: si.Id, HSNCode: si.HSNCode || '', GSTRate: gstRate,
          StarRating: si.StarRating || 0,
          Quantity: quantity, Rate: rate, Amount: amount,
          DiscountPercentage: discPct, DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
          SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
          IGSTAmount: 0, TotalAmount: Math.round(netAmount * 100) / 100,
          WhichMOP: null, IsActive: true
        };
      });
    const updatedDetails = [...enquiryDetails, ...newItems];
    setEnquiryDetails(updatedDetails);
    calculateTotals(updatedDetails);
    handleCloseGetItemsModal();
  };

  // Save handler
  const handleSave = async () => {
    if (!formData.CustomerName?.trim()) { setError('Customer Name is required'); return; }
    if (!formData.ContactNumber?.trim()) { setError('Contact Number is required'); return; }
    if (enquiryDetails.length === 0) { setError('Please add at least one item'); return; }
    const hasInvalid = enquiryDetails.some(d => !d.ItemId || d.Quantity <= 0);
    if (hasInvalid) { setError('Please fill all item details (Item and Quantity required)'); return; }

    setLoading(true);
    setLoadingMessage('Saving enquiry...');

    // WhichMOP label → int (1=MOP1, 2=MOP2, ... 5=MOP5, 6=MRP, 7=SaleRate)
    const whichMopToInt = (val) => {
      if (val === null || val === undefined || val === '') return null;
      if (typeof val === 'number') return val;
      const map = { 'MOP1': 1, 'MOP2': 2, 'MOP3': 3, 'MOP4': 4, 'MOP5': 5, 'MRP': 6, 'Sale Rate': 7 };
      return map[val] ?? null;
    };

    try {
      const enquiryVM = {
        Enquiry: {
          ...formData, CompanyId: companyId, BranchId: branchId || 1,
          IsConfirmOrder: isCreateMode ? false : (formData.IsConfirmOrder ?? false),
          TotalQuantity: enquiryDetails.reduce((sum, d) => sum + (parseFloat(d.Quantity) || 0), 0),
          TotalAmount: enquiryDetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0),
          CreatedBy: isCreateMode ? userId : (formData.CreatedBy || userId),
          ModifiedBy: userId,
          CreatedDate: isCreateMode ? new Date().toISOString() : (formData.CreatedDate || new Date().toISOString()),
          ModifiedDate: new Date().toISOString()
        },
        EnquiryDetails: enquiryDetails.map(d => ({
          Id: d.Id || 0,
          EnquiryId: formData.Id || 0,
          ItemId: d.ItemId,
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          Quantity: parseFloat(d.Quantity) || 0,
          Rate: parseFloat(d.Rate) || 0,
          Amount: parseFloat(d.Amount) || 0,
          DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(d.DiscountAmount) || 0,
          TaxableAmount: parseFloat(d.TaxableAmount) || 0,
          CGSTAmount: parseFloat(d.CGSTAmount) || 0,
          SGSTAmount: parseFloat(d.SGSTAmount) || 0,
          IGSTAmount: parseFloat(d.IGSTAmount) || 0,
          TotalAmount: parseFloat(d.TotalAmount) || 0,
          WhichMOP: whichMopToInt(d.WhichMOP),
          StarRating: d.StarRating || 0,
          IsActive: d.IsActive ?? true,
          CompanyId: companyId,
          BranchId: branchId || 1,
          CreatedBy: isCreateMode ? userId : (d.CreatedBy || userId),
          ModifiedBy: userId,
          CreatedDate: isCreateMode ? new Date().toISOString() : (d.CreatedDate || new Date().toISOString()),
          ModifiedDate: new Date().toISOString()
        }))
      };

      if (isCreateMode) {
        await postData('EnquiryApi', '', enquiryVM, token);
        setSuccess('Enquiry created successfully!');
      logAudit(AUDIT_ACTION.CREATE, 'EnquiryApi', 0, null, { CustomerName: formData.CustomerName || '', BillNumber: formData.BillNumber || '' });
      } else {
        await updateData('EnquiryApi', formData.Id, enquiryVM, token);
        setSuccess('Enquiry updated successfully!');
      logAudit(AUDIT_ACTION.UPDATE, 'EnquiryApi', formData.Id, { ...originalFormData }, { ...formData });
      }
      setTimeout(() => history.push('/EnquiryIndex'), 1000);
    } catch (e) {
      console.error('Save error response data:', e?.response?.data);
      const msg = e?.response?.data?.message || e?.response?.data?.title
        || (typeof e?.response?.data === 'string' ? e.response.data : null)
        || e?.message || 'Error saving enquiry';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Filtered items for Get Items modal
  const filteredItems = useMemo(() => {
    const source = (filterBrandId || filterCategoryId) ? filteredByCategory : items;
    if (!getItemsSearch.trim()) return source;
    const term = getItemsSearch.toLowerCase().trim();
    return source.filter(item =>
      item.Name?.toLowerCase().includes(term) ||
      item.HSNCode?.toLowerCase().includes(term) ||
      item.Code?.toLowerCase().includes(term)
    );
  }, [items, filteredByCategory, filterBrandId, filterCategoryId, getItemsSearch]);

  const paginatedItems = useMemo(() => {
    const start = (currentItemsPage - 1) * itemsPerPage;
    return filteredItems.slice(start, start + itemsPerPage);
  }, [filteredItems, currentItemsPage, itemsPerPage]);

  const totalItemPages = Math.ceil(filteredItems.length / itemsPerPage);
  const isItemInDetails = (itemId) => enquiryDetails.some(d => d.ItemId === parseInt(itemId));

  const itemNameMap = useMemo(() => {
    const map = {};
    items.forEach(i => { map[i.Id] = i.Code ? `${i.Code} - ${i.Name}` : i.Name; });
    return map;
  }, [items]);

  const formatCurrency = (amt) => `₹${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <LogoLoader open={true} message={loadingMessage} />;

  return (
    <div className="enquiry-form-page">
      {/* Snackbars */}
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>

      {/* Top Header Bar */}
      <div className="enq-form-header">
        <div className="enq-form-header-left">
          <IconButton size="small" onClick={handleBack} sx={{ color: '#1e40af' }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
            {title}
          </Typography>
          {hasUnsavedChanges && !isViewMode && (
            <Chip label="Unsaved" size="small" sx={{ fontSize: '0.6rem', height: '18px', bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }} />
          )}
        </div>
        {!isViewMode && (
          <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave}
            sx={{ textTransform: 'none', fontSize: '0.8rem', bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, borderRadius: '8px' }}>
            Save
          </Button>
        )}
      </div>

      {/* Form Content */}
      <div className="enq-form-content">
        {/* Customer Info */}
        <div className="enq-form-section">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setCustomerExpanded(!customerExpanded)}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af' }}>
              ENQUIRY INFO
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {!customerExpanded && formData.CustomerName && (
                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 500 }}>
                  {formData.CustomerName}
                </Typography>
              )}
              <IconButton size="small" sx={{ p: 0.3 }}>
                {customerExpanded ? <ExpandLess sx={{ fontSize: 18, color: '#64748b' }} /> : <ExpandMore sx={{ fontSize: 18, color: '#64748b' }} />}
              </IconButton>
            </Box>
          </Box>

          {/* Always show Enquiry No & Date */}
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>No:</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af' }}>{formData.EnquiryNo || '-'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, justifyContent: 'flex-end' }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Date:</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b' }}>{formData.EnquiryDate || '-'}</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Collapsible customer fields */}
          {customerExpanded && (
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Customer Name *" value={formData.CustomerName || ''}
                  disabled={isViewMode} onChange={(e) => handleChange('CustomerName', e.target.value)}
                  placeholder="Enter customer name"
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 1 } }} />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={customers}
                  value={selectedCustomer}
                  onChange={handleSelectCustomer}
                  disabled={isViewMode}
                  getOptionLabel={(option) => {
                    const phone = option.ContactNumber1 || '';
                    const name = option.Name || option.CustomerName || '';
                    return `${phone} - ${name}`;
                  }}
                  filterOptions={(options, { inputValue }) => {
                    const term = inputValue.toLowerCase();
                    return options.filter(c =>
                      (c.ContactNumber1 || '').includes(term) ||
                      (c.Name || c.CustomerName || '').toLowerCase().includes(term)
                    ).slice(0, 20);
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 0.8, px: 1.5 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>
                        {option.Name || option.CustomerName || '-'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
                        📞 {option.ContactNumber1 || '-'}
                        {option.Address ? ` • ${option.Address}` : ''}
                      </Typography>
                    </Box>
                  )}
                  freeSolo
                  onInputChange={(event, value, reason) => {
                    if (reason === 'input') {
                      const numericValue = value.replace(/[^0-9]/g, '');
                      setFormData(prev => ({ ...prev, ContactNumber: numericValue, MasterId: null }));
                      setCustomerFound(false);
                      setSelectedCustomer(null);
                    }
                  }}
                  inputValue={formData.ContactNumber || ''}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Contact Number *"
                      placeholder="Search customer by phone or name"
                      helperText={customerFound ? '✓ Customer selected' : ''}
                      FormHelperTextProps={{ sx: { color: '#059669', fontSize: '0.7rem', mt: 0.3 } }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }}
                      inputProps={{ ...params.inputProps, inputMode: 'numeric', maxLength: 15 }} />
                  )}
                  noOptionsText="No customer found — type manually"
                  sx={{ '& .MuiAutocomplete-listbox': { maxHeight: 200 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Address" value={formData.Address || ''}
                  disabled={isViewMode} onChange={(e) => handleChange('Address', e.target.value)}
                  placeholder="Enter address"
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 1 } }} />
              </Grid>
            </Grid>
          )}
        </div>

        <Divider sx={{ my: 1 }} />

        {/* Item Details */}
        <div className="enq-form-section">

          {/* Card 1 — Brand & Category filter */}
          {!isViewMode && (
            <Box sx={{ mb: 1.5, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
              {/* Header row — clickable to expand/collapse */}
              <Box
                onClick={() => setFilterExpanded(prev => !prev)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1.25, cursor: 'pointer', userSelect: 'none' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    Filter
                  </Typography>
                  {/* Show active filter chips when collapsed */}
                  {!filterExpanded && (filterBrandId || filterCategoryId) && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {filterBrandId && (
                        <Chip size="small" label={brands.find(b => b.Id === filterBrandId)?.Name || 'Brand'}
                          sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }} />
                      )}
                      {filterCategoryId && (
                        <Chip size="small" label={categories.find(c => c.Id === filterCategoryId)?.Name || 'Category'}
                          sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }} />
                      )}
                    </Box>
                  )}
                </Box>
                <IconButton size="small" sx={{ p: 0.3, color: '#64748b' }}>
                  {filterExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>

              {/* Collapsible content */}
              {filterExpanded && (
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, width: '100%' }}>

                    <Autocomplete
                      options={categories}
                      value={categories.find(c => c.Id === filterCategoryId) || null}
                      onChange={(e, val) => setFilterCategoryId(val ? val.Id : null)}
                      getOptionLabel={(option) => option.Name || ''}
                      size="small"
                      sx={{ flex: 1, minWidth: 0 }}
                      renderInput={(params) => (
                        <TextField {...params} label="Category" placeholder="Search..."
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.82rem' } }} />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ fontSize: '0.82rem', py: 0.5, px: 1.5 }}>{option.Name}</Box>
                      )}
                      noOptionsText="No categories"
                      clearOnEscape
                    />

                    <Autocomplete
                      options={brands}
                      value={brands.find(b => b.Id === filterBrandId) || null}
                      onChange={(e, val) => setFilterBrandId(val ? val.Id : null)}
                      getOptionLabel={(option) => option.Name || ''}
                      size="small"
                      sx={{ flex: 1, minWidth: 0 }}
                      renderInput={(params) => (
                        <TextField {...params} label="Brand" placeholder="Search..."
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.82rem' } }} />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ fontSize: '0.82rem', py: 0.5, px: 1.5 }}>{option.Name}</Box>
                      )}
                      noOptionsText="No brands"
                      clearOnEscape
                    />

                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Card 2 — Items list + Get Items */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Items ({enquiryDetails.length})
              </Typography>
              {!isViewMode && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<List />}
                  onClick={handleOpenGetItemsModal}
                  sx={{
                    fontSize: '0.75rem', py: 0.6, px: 1.5,
                    textTransform: 'none', bgcolor: '#1e40af',
                    '&:hover': { bgcolor: '#1d4ed8' },
                    borderRadius: '8px'
                  }}
                >
                  Get Items
                </Button>
              )}
            </Box>

            {/* Mobile-friendly Item Cards */}
            <div className="enq-items-list">
              {enquiryDetails.length === 0 ? (
                <div className="enq-empty-items">
                  <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 3 }}>
                    No items added. Tap "Get Items" to add.
                  </Typography>
                </div>
              ) : (
                enquiryDetails.map((detail, index) => (
                  <div className="enq-item-card" key={index}>
                    <div className="enq-item-card-top">
                      <div className="enq-item-card-name">
                        <span className="enq-item-num">{index + 1}.</span>
                        <span className="enq-item-name"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleOpenOffersModal(detail.ItemId)}>
                          {itemNameMap[detail.ItemId] || 'Unknown Item'}
                        </span>
                      </div>
                      {!isViewMode && (
                        <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ color: '#dc2626', p: 0.3 }}>
                          <Delete sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </div>
                    <div className="enq-item-card-info">
                      {/* <span className="enq-item-hsn">HSN: {detail.HSNCode || '-'}</span> */}
                      <span className="enq-item-gst">GST: {detail.GSTRate || 0}%</span>
                      <span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '1px 6px', borderRadius: '4px', background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24', cursor: 'pointer' }}
                        onClick={() => handleOpenOffersModal(detail.ItemId)}>
                        🎁 Offers/Warranty
                      </span>
                      {/* Star Rating (readonly) */}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        {[1, 2, 3, 4, 5].map(star => (
                          <span
                            key={star}
                            style={{
                              fontSize: '0.75rem',
                              color: star <= (detail.StarRating || 0) ? '#f59e0b' : '#d1d5db',
                              cursor: 'default',
                              lineHeight: 1,
                            }}
                          >★</span>
                        ))}
                        {detail.StarRating > 0 && (
                          <span style={{ fontSize: '0.6rem', color: '#92400e', fontWeight: 700, marginLeft: 2 }}>
                            {detail.StarRating}/5
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="enq-item-card-fields">
                      <div className="enq-item-field">
                        <label>Qty</label>
                        <input type="number" value={detail.Quantity || ''} disabled={isViewMode}
                          onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} />
                      </div>
                      <div className="enq-item-field">
                        <label>Rate</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <input
                            type="number"
                            value={detail.Rate || ''}
                            disabled={isViewMode}
                            onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)}
                            style={{ flex: 1 }}
                          />
                          {!isViewMode && detail.ItemId && (
                            <button
                              onClick={(e) => handleMopDropdownOpen(e, index)}
                              title="Select MOP"
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: 26, height: 26, padding: 0, border: 'none',
                                borderRadius: 6, cursor: 'pointer', flexShrink: 0,
                                background: detail.WhichMOP ? '#1e40af' : '#e0e7ff',
                                color: detail.WhichMOP ? '#fff' : '#1e40af',
                                fontSize: '0.6rem', fontWeight: 700,
                                transition: 'background 0.15s',
                              }}
                            >
                              {detail.WhichMOP
                                ? String(detail.WhichMOP).replace('MOP', 'M').replace('Sale Rate', 'SR').replace(/^(\d+)$/, (_, n) => ({ '1': 'M1', '2': 'M2', '3': 'M3', '4': 'M4', '5': 'M5', '6': 'MRP', '7': 'SR' }[n] || `M${n}`))
                                : <KeyboardArrowDown style={{ fontSize: 14 }} />}
                            </button>
                          )}
                        </div>
                        {/* Selected MOP chip */}
                        {detail.WhichMOP && (
                          <div style={{ marginTop: 3 }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                              fontSize: '0.6rem', fontWeight: 700, padding: '2px 6px',
                              borderRadius: 10, background: '#dbeafe', color: '#1e40af',
                              border: '1px solid #93c5fd',
                            }}>
                              ✓ {typeof detail.WhichMOP === 'number'
                                ? ({ 1: 'MOP1', 2: 'MOP2', 3: 'MOP3', 4: 'MOP4', 5: 'MOP5', 6: 'MRP', 7: 'Sale Rate' }[detail.WhichMOP] || detail.WhichMOP)
                                : detail.WhichMOP}
                              {!isViewMode && (
                                <span
                                  onClick={() => {
                                    setEnquiryDetails(prev => {
                                      const u = [...prev];
                                      u[index] = { ...u[index], WhichMOP: null };
                                      return u;
                                    });
                                  }}
                                  style={{ cursor: 'pointer', marginLeft: 2, color: '#6b7280', fontWeight: 400, fontSize: '0.65rem' }}
                                >×</span>
                              )}
                            </span>
                          </div>
                        )}
                        {/* Selected MOP chip */}
                        {/* {detail.WhichMOP && (
                          <div style={{ marginTop: 3 }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                              fontSize: '0.6rem', fontWeight: 700, padding: '2px 6px',
                              borderRadius: 10, background: '#dbeafe', color: '#1e40af',
                              border: '1px solid #93c5fd',
                            }}>
                              ✓ {
                                typeof detail.WhichMOP === 'number'
                                  ? ({ 1: 'MOP1', 2: 'MOP2', 3: 'MOP3', 4: 'MOP4', 5: 'MOP5', 6: 'MRP', 7: 'Sale Rate' }[detail.WhichMOP] || detail.WhichMOP)
                                  : detail.WhichMOP
                              }
                              {!isViewMode && (
                                <span
                                  onClick={() => {
                                    setEnquiryDetails(prev => {
                                      const u = [...prev];
                                      u[index] = { ...u[index], WhichMOP: null };
                                      return u;
                                    });
                                  }}
                                  style={{ cursor: 'pointer', marginLeft: 2, color: '#6b7280', fontWeight: 400, fontSize: '0.65rem' }}
                                >×</span>
                              )}
                            </span>
                          </div>
                        )} */}
                      </div>
                      <div className="enq-item-field enq-item-amount">
                        <label>Amount</label>
                        <span>{(parseFloat(detail.TotalAmount) || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Box> {/* end Card 2 */}
        </div>

        {/* Totals */}
        {enquiryDetails.length > 0 && (
          <div className="enq-form-totals">
            <div className="enq-total-row">
              <span>Total Quantity</span>
              <span className="enq-total-value">{formData.TotalQuantity || 0}</span>
            </div>
            <div className="enq-total-row">
              <span>Taxable Amount</span>
              <span className="enq-total-value">
                {formatCurrency(enquiryDetails.reduce((s, d) => s + (parseFloat(d.TaxableAmount) || 0), 0))}
              </span>
            </div>
            <div className="enq-total-row">
              <span>Total GST (CGST + SGST)</span>
              <span className="enq-total-value enq-gst">
                {formatCurrency(enquiryDetails.reduce((s, d) => s + (parseFloat(d.CGSTAmount) || 0) + (parseFloat(d.SGSTAmount) || 0), 0))}
              </span>
            </div>
            <Divider sx={{ my: 0.5 }} />
            <div className="enq-total-row enq-grand-total">
              <span>Grand Total</span>
              <span>{formatCurrency(formData.TotalAmount)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Back Confirmation */}
      <Dialog open={backConfirmOpen} onClose={() => setBackConfirmOpen(false)} maxWidth="xs">
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#d97706' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Unsaved Changes</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">You have unsaved changes. Discard and go back?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackConfirmOpen(false)} size="small">Stay</Button>
          <Button onClick={() => history.push('/EnquiryIndex')} color="error" size="small" variant="contained">Discard</Button>
        </DialogActions>
      </Dialog>

      {/* Get Items Modal — mobile bottom sheet */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            m: 0,
            borderRadius: '16px 16px 0 0',
            maxHeight: 'calc(82vh - env(safe-area-inset-bottom))',
            height: 'calc(82vh - env(safe-area-inset-bottom))',
            display: 'flex',
            flexDirection: 'column',
            mb: 'calc(62px + env(safe-area-inset-bottom))',
            overflow: 'hidden',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.15)',
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #f0f0f0', bgcolor: '#fff', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={handleCloseGetItemsModal}
                sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' }, borderRadius: '8px' }}>
                <ArrowBack sx={{ fontSize: 18 }} />
              </IconButton>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Select Items</Typography>
            </Box>
            {selectedItems.length > 0 && (
              <Button size="small" variant="contained" onClick={handleAddSelectedItems}
                sx={{
                  textTransform: 'none', fontSize: '0.78rem', bgcolor: '#2C3E6E', borderRadius: '8px',
                  '&:hover': { bgcolor: '#1a2d5a' }
                }}>
                Add ({selectedItems.length})
              </Button>
            )}
          </Box>
        </DialogTitle>

        {/* Scrollable content */}
        <DialogContent sx={{ p: 0, flex: 1, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>

          {/* Search */}
          <Box sx={{ px: 1.5, pt: 1.5, pb: 1, flexShrink: 0 }}>
            <TextField
              fullWidth size="small"
              placeholder="Search items by name, code or HSN.."
              value={getItemsSearch}
              onChange={(e) => { setGetItemsSearch(e.target.value); setCurrentItemsPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 17, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px', bgcolor: '#f8fafc', fontSize: '0.85rem',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#cbd5e1' },
                  '&.Mui-focused fieldset': { borderColor: '#2C3E6E' },
                },
              }}
            />
          </Box>

          {/* Table */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 1 }}>
                  <th style={{ padding: '8px 10px', fontWeight: 700, color: '#475569', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', width: 36 }}>✓</th>
                  <th style={{ padding: '8px 10px', fontWeight: 700, color: '#475569', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left' }}>Item Name</th>
                  <th style={{ padding: '8px 10px', fontWeight: 700, color: '#475569', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>Rate</th>
                  <th style={{ padding: '8px 10px', fontWeight: 700, color: '#475569', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>GST%</th>
                </tr>
              </thead>
              <tbody>
                {filterLoading ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8', fontSize: '0.85rem' }}>
                      Loading items...
                    </td>
                  </tr>
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8', fontSize: '0.85rem' }}>
                      No items found
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((item, idx) => {
                    const isSelected = selectedItems.some(s => s.Id === item.Id);
                    const isInDetails = isItemInDetails(item.Id);
                    return (
                      <tr
                        key={item.Id}
                        onClick={() => !isInDetails && handleItemSelection(item, !isSelected)}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          background: isSelected ? '#eff6ff' : idx % 2 === 0 ? '#fff' : '#fafbfc',
                          opacity: isInDetails ? 0.45 : 1,
                          cursor: isInDetails ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <td style={{ padding: '10px 10px' }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isInDetails}
                            onChange={(e) => handleItemSelection(item, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: 14, height: 14, accentColor: '#2C3E6E', cursor: isInDetails ? 'not-allowed' : 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '10px 10px' }}>
                          <div style={{ fontWeight: isSelected ? 600 : 400, color: '#1e293b', fontSize: '0.78rem', lineHeight: 1.3 }}>
                            {item.Code ? `${item.Code} - ` : ''}{item.Name}
                          </div>
                          {item.HSNCode && (
                            <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '2px' }}>
                              HSN: {item.HSNCode}
                            </div>
                          )}
                          {/* Star Rating (readonly) */}
                          {item.StarRating > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '3px' }}>
                              {[1, 2, 3, 4, 5].map(star => (
                                <span key={star} style={{ fontSize: '0.72rem', color: star <= item.StarRating ? '#f59e0b' : '#d1d5db', lineHeight: 1 }}>★</span>
                              ))}
                              <span style={{ fontSize: '0.6rem', color: '#92400e', fontWeight: 700, marginLeft: 2 }}>{item.StarRating}/5</span>
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '10px 10px', color: '#1e293b', fontWeight: 600, textAlign: 'right', fontSize: '0.78rem' }}>
                          {(() => {
                            const mop1 = parseFloat(item.MOP1) || 0;
                            const rate = mop1 > 0 ? mop1 : (parseFloat(item.SaleRate) || 0);
                            return `₹${rate}`;
                          })()}
                        </td>
                        <td style={{ padding: '10px 10px', color: '#059669', fontWeight: 600, textAlign: 'right', fontSize: '0.78rem' }}>
                          {item.GSTRate || 0}%
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Box>

          {/* Pagination */}
          {totalItemPages > 1 && (
            <Box sx={{ px: 1.5, py: 1, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
              <Pagination count={totalItemPages} page={currentItemsPage} onChange={(_, p) => setCurrentItemsPage(p)} size="small" />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Offers/Warranty Modal */}
      <Dialog open={offersModalOpen} onClose={() => setOffersModalOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #1e3a6e 0%, #2c4f8c 100%)', px: 2.5, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#fff' }}>🎁 Offers & Warranty</Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#cbd5e1', mt: 0.3 }}>{offersModalItemName}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setOffersModalOpen(false)} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
              <ArrowBack />
            </IconButton>
          </Box>
        </Box>
        <DialogContent sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          {offersModalData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: '2rem', mb: 1 }}>📭</Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>No offers or warranty available for this item.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {offersModalData.map((item, idx) => (
                <Box key={idx} sx={{
                  p: 2, borderRadius: '12px', bgcolor: '#fff',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  transition: 'transform 0.1s, box-shadow 0.1s',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }
                }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '1.1rem' }}>🏷️</span>
                  </Box>
                  <Typography sx={{ fontSize: '0.88rem', color: '#1e293b', fontWeight: 500, lineHeight: 1.4 }}>
                    {item.OffersAndWarranty || '-'}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* MOP Dropdown Popover */}
      <Popover
        open={Boolean(mopAnchorEl)}
        anchorEl={mopAnchorEl}
        onClose={handleMopDropdownClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(30,64,175,0.18)',
            border: '1px solid #dbeafe',
            minWidth: 210,
            overflow: 'hidden',
          }
        }}
      >
        {/* Popover header */}
        <Box sx={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', px: 1.75, py: 1.1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#fff' }}>
            💰 Select Rate (MOP)
          </Typography>
          {mopPopoverIndex !== null && (
            <Typography sx={{ fontSize: '0.65rem', color: '#bfdbfe', mt: 0.2 }} noWrap>
              {itemNameMap[enquiryDetails[mopPopoverIndex]?.ItemId] || ''}
            </Typography>
          )}
        </Box>

        {/* Options */}
        <Box sx={{ py: 0.5 }}>
          {mopRowLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2.5, gap: 1 }}>
              <CircularProgress size={16} sx={{ color: '#1e40af' }} />
              <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>Loading...</Typography>
            </Box>
          ) : mopRowOptions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>No rate options available</Typography>
            </Box>
          ) : (
            mopRowOptions.map((option, idx) => {
              const currentWhichMOP = mopPopoverIndex !== null ? enquiryDetails[mopPopoverIndex]?.WhichMOP : null;
              const isSelected = currentWhichMOP === option.label;
              // The last item in visible list that is NOT selected = "next unlock" hint
              const isNextReveal = !isSelected && idx === mopRowOptions.length - 1 && currentWhichMOP !== null;
              const shortLabel = option.label.replace('MOP', 'M').replace('Sale Rate', 'SR');
              return (
                <Box
                  key={idx}
                  onClick={() => handleMopSelect(option)}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 1.75, py: 0.85, cursor: 'pointer',
                    bgcolor: isSelected ? '#eff6ff' : isNextReveal ? '#fefce8' : 'transparent',
                    borderLeft: isSelected ? '3px solid #1e40af' : isNextReveal ? '3px solid #f59e0b' : '3px solid transparent',
                    transition: 'background 0.12s',
                    '&:hover': { bgcolor: isNextReveal ? '#fef9c3' : '#f0f9ff' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '7px',
                      bgcolor: isSelected ? '#1e40af' : isNextReveal ? '#fef3c7' : '#e0e7ff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: isSelected ? '#fff' : isNextReveal ? '#92400e' : '#1e40af' }}>
                        {shortLabel}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: isSelected ? 700 : 500, color: '#1e293b', lineHeight: 1.2 }}>
                        {option.label}
                      </Typography>
                      {isNextReveal && (
                        <Typography sx={{ fontSize: '0.6rem', color: '#92400e', fontWeight: 600 }}>
                          {option.label === 'MRP' ? 'actual rate' : 'lower rate'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#059669', lineHeight: 1.2 }}>
                        ₹{option.value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                      {/* {option.pct != null && (
                        <Typography sx={{ fontSize: '0.6rem', color: '#6b7280', fontWeight: 600, lineHeight: 1 }}>
                          {option.pct}%
                        </Typography>
                      )} */}
                    </Box>
                    {isSelected && <Check sx={{ fontSize: 14, color: '#1e40af' }} />}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>

      {/* Rate Selection Modal (MOP Options) — legacy, kept for safety */}
      <Dialog open={rateModalOpen} onClose={() => setRateModalOpen(false)} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', px: 2.5, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#fff' }}>💰 Select Rate</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#bfdbfe', mt: 0.3 }}>{rateModalItemName}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setRateModalOpen(false)} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
              <ArrowBack />
            </IconButton>
          </Box>
        </Box>
        <DialogContent sx={{ p: 2, bgcolor: '#f8fafc' }}>
          {rateModalLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={28} sx={{ color: '#1e40af' }} />
              <Typography sx={{ color: '#64748b', fontSize: '0.85rem', mt: 1.5 }}>Loading rate options...</Typography>
            </Box>
          ) : rateOptions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>📭</Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.85rem' }}>No rate options available for this item.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {rateOptions.map((option, idx) => (
                <Box key={idx} onClick={() => handleRateSelect(option.value, option.label)} sx={{
                  p: 1.5, borderRadius: '10px', bgcolor: '#fff',
                  border: '1px solid #e2e8f0', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: '#eff6ff', borderColor: '#3b82f6', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(59,130,246,0.15)' }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#1e40af' }}>{option.label.replace('MOP', 'M')}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{option.label}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#059669' }}>
                    ₹{option.value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnquiryForm;
